<template>
  <div class="sm:bg-bgImage-2 bg-white">
    <!-- Page Container -->
    <div class="mx-auto max-w-2xl md:pt-8 pb-2 sm:px-6 lg:max-w-7xl lg:px-8">
      <div class="md:mt-6">
        <!-- Basic Info Section -->
        <div class="space-y-8">
          <!-- Doctor Basic Info Card -->
          <div class="bg-white rounded-md shadow-sm">
            <div class="grid gap-6 lg:grid-cols-12 sm:grid-cols-12 px-4 py-6 sm:px-6 lg:p-8">
              <!-- Doctor Photo -->
              <div class="sm:flex lg:col-span-3 sm:col-span-4 flex justify-center lg:justify-start">
                <div
                  class="aspect-h-1 aspect-w-1 w-full flex-shrink-0 overflow-hidden rounded-lg sm:aspect-none sm:h-60 sm:w-40 h-60 lg:h-80 lg:w-60">
                  <img :src="doctorDetails.provider.doctor_profile_image_url
                    ? doctorDetails.provider.doctor_profile_image_url
                    : 'https://s3.amazonaws.com/cms-media.haodrs.com/no_image_icon_md_be41a8f87f.png'
                    " :alt="doctorDetails.provider.doctor_profile_image_url
                      ? `${doctorDetails.provider.cn_name}医生头像`
                      : 'No Image Placeholder Doctor'
                      " class="h-full w-full object-cover object-top sm:h-full sm:w-full" />
                </div>
              </div>

              <!-- Doctor Name and Basic Info -->
              <div class="lg:col-span-9 sm:col-span-8 flex flex-col space-y-4">
                <div class="mt-2 sm:mt-0">
                  <h3 class="text-3xl font-medium text-primaryText flex items-center">
                    <span>{{ doctorDetails.provider.cn_name }} 医师</span>
                    <HandThumbUpIcon class="h-5 w-5 ml-2 text-green fill-green-500" aria-hidden="true"
                      v-if="doctorDetails.provider.linked_user_owner" />
                  </h3>

                  <NuxtLink :to="`/specialty/${doctorDetails.provider.en_specialty_1}`"
                    class="mt-2 text-lg text-gray-900 pb-1 uppercase inline-block">
                    {{ doctorDetails.provider.cn_specialty_1 }}医生 |
                    {{ doctorDetails.provider.en_specialty_1 }} Doctor
                  </NuxtLink>
                </div>

                <!-- AIGC (SEO, Website, Languages) Section -->
                <div v-if="doctorDetails.provider.aigc_v3">
                  <!-- SEO Keywords Section -->
                  <div v-if="seoKeywordsChinese.length" class="mt-0 p-2 bg-primaryBG-100 rounded-md shadow-sm">
                    <!-- Chinese Keywords -->
                    <div class="flex flex-wrap gap-2 mb-2">
                      <span v-for="(kw, idx) in seoKeywordsChinese" :key="idx"
                        class="inline-flex items-center rounded-full bg-primaryBG-200 px-3 py-1 text-xs sm:text-sm font-medium text-primaryBG-700">
                        #{{ kw }}
                      </span>
                    </div>

                    <!-- Toggle for English if available -->
                    <div v-if="seoKeywordsEnglish.length" class="mt-2">
                      <button
                        class="px-3 py-1 text-xs  text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                        @click="showEnglishSEOKeywords = !showEnglishSEOKeywords">
                        {{ showEnglishSEOKeywords ? 'Hide English' : 'Show English' }}
                      </button>
                      <transition name="fade">
                        <div v-if="showEnglishSEOKeywords" class="mt-4 space-y-2">
                          <div class="flex flex-wrap gap-2">
                            <span v-for="(kw, idx) in seoKeywordsEnglish" :key="idx"
                              class="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs sm:text-sm font-medium text-blue-800">
                              #{{ kw }}
                            </span>
                          </div>
                        </div>
                      </transition>
                    </div>
                  </div>



                  <!-- Supported Languages -->

                </div>
                <!-- End AIGC (SEO, Website, Languages) Section -->

                <!-- Clinic Address & Phone -->
                <div>
                  <p class="mt-2 text-md text-gray-600">
                    <span class="font-semibold">{{ $t("doctor.detail_page.clinic_address") }}:</span>
                    <br />
                  <div v-if="doctorDetails.facilities?.length > 1" class="space-y-2">
                    <div class="text-sm text-gray-500">请选择就诊地点：</div>
                    <div class="flex flex-col gap-2">
                      <button v-for="(fac, idx) in doctorDetails.facilities" :key="fac._id || idx" type="button"
                        class="w-full text-left rounded-md border border-gray-200 px-3 py-2 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primaryBtn"
                        :aria-label="`选择诊所：${fac.en_full_address || fac.cn_full_address || '诊所'}`" tabindex="0"
                        @click="handleSelectFacility(String(fac._id))"
                        @keydown.enter.prevent="handleSelectFacility(String(fac._id))"
                        @keydown.space.prevent="handleSelectFacility(String(fac._id))">
                        <div class="text-sm font-medium text-primaryText">{{ fac.en_full_address || fac.cn_full_address
                        }}</div>
                      </button>
                    </div>
                  </div>
                  <NuxtLink v-else :to="{ hash: '#clinic-map' }"
                    class="mt-2 inline-flex items-center rounded-md bg-primaryBtn px-4 py-2 text-sm text-white shadow-sm hover:bg-primaryBtn/20 hover:text-primaryBtn hover:border-1">
                    {{ doctorDetails.facilities[0].en_full_address }}</NuxtLink>
                  </p>
                  <!-- <div class="text-xs text-primaryText/60 font-thin">点击地址来查看地图</div> -->
                </div>

                <!-- Clinic Phone or Virtual Number -->
                <div class="mt-4">
                  <p class="text-md text-gray-600">
                    <span class="font-semibold">{{ $t("doctor.detail_page.clinic_phone") }}:</span>
                    <br />
                    <span v-if="doctorDetails.facilities[0].telephone_tracking_hys_1"
                      class="mt-2 text-base sm:text-lg text-primaryText">{{
                        doctorDetails.facilities[0].telephone_tracking_hys_1
                      }}</span>

                    <span v-else-if="virtualNumber" class=" block text-xl sm:text-2xl font-semibold text-primaryText">
                      <a :href="`tel:${virtualNumber}`" class="underline hover:text-primaryColor/80">
                        {{ displayPhoneNumber }}

                      </a>
                      <div class="text-xs text-primaryText/60 font-semibold">点击号码来拨打电话

                        <br /><span class="font-thin">为了让您得到更好的医疗服务体验，电话可能会被录音或转译</span>
                        <br /><span class="font-thin">如您与诊所沟通时遇到困难，您也可<NuxtLink class="underline text-primaryColor"
                            to="/contact/concierge">联系北美好医生平台的健康管家</NuxtLink>获得帮助</span>
                      </div>
                    </span>

                    <span v-else class="mt-1 block text-xl sm:text-2xl text-primaryText">
                      <button @click="getVirtualNumber"
                        class="mt-1 inline-flex items-center rounded-md bg-primaryBtn px-4 py-2 text-sm text-white shadow-sm hover:bg-primaryBtn/20 hover:text-primaryBtn">
                        <PhoneIcon class="mr-1 h-5 w-5 inline" aria-hidden="true" />
                        点击来获取诊所电话号码
                      </button>
                    </span>

                  </p>
                </div>


                <!-- Business Hours -->
                <div class="mt-5">
                  <p class="text-md text-gray-600">
                    <span class="font-semibold">诊所营业时间:</span>
                    <br />
                    <span class="mt-3 text-sm sm:text-md text-primaryText">{{
                      doctorDetails.provider.aigc_v3?.chat_response?.business_hours || '请致电诊所进行预约咨询' }}</span>
                  </p>
                </div>
                <div v-if="languagesChinese.length" class="mt-2">
                  <h4 class="text-md text-gray-600">
                    <span class="font-semibold">诊所支持语言:</span>
                  </h4>
                  <div class="flex flex-wrap gap-2 mb-2">
                    <span v-for="(lang, idx) in languagesChinese" :key="idx"
                      class="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-xs sm:text-sm font-medium text-gray-700">
                      {{ lang }}
                    </span>
                  </div>
                  <div v-if="languagesEnglish.length" class="mt-2">
                    <button
                      class="px-3 py-1 text-xs text-blue-600 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
                      @click="showEnglishLanguages = !showEnglishLanguages">

                      {{ showEnglishLanguages ? 'Hide English' : 'Show English' }}
                    </button>
                    <transition name="fade">

                      <div v-if="showEnglishLanguages" class="mt-4">
                        <h4 class="text-md text-gray-600">
                          Clinic Supported Languages
                        </h4>
                        <div class="flex flex-wrap gap-2">
                          <span v-for="(lang, idx) in languagesEnglish" :key="idx"
                            class="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-xs sm:text-sm font-medium text-gray-700">
                            {{ lang }}
                          </span>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>


            </div>
          </div>
          <!-- End Doctor Basic Info Card -->

<!-- Doctor Blogs Card (single card; subsections are conditional; roles from API) -->
<div
  v-if="doctorBlogs && (doctorBlogs.authored?.length || doctorBlogs.collaborated?.length)"
  class="bg-white p-4 sm:p-6 lg:p-8 rounded-md shadow-sm mt-6"
>
  <h2 class="text-xl sm:text-2xl font-semibold text-primaryText mb-4">
    博客
  </h2>

  <!-- Authored first -->
  <div v-if="doctorBlogs.authored?.length" class="mb-6">
    <h3 class="text-lg font-semibold text-primaryText mb-3">主笔文章</h3>
    <ul class="space-y-2">
      <li
        v-for="(b, i) in doctorBlogs.authored"
        :key="'auth-'+(b._id?.$oid || b._id || i)"
        class="flex items-start justify-between rounded-md border border-gray-200 px-3 py-2 hover:bg-gray-50"
      >
        <NuxtLink class="text-primaryColor underline" :to="`/blogs/${encodeURIComponent(b.slug)}`">
          {{ b.title }}
        </NuxtLink>
        <span class="text-xs text-gray-500 ml-3 whitespace-nowrap">
          {{ formatBlogDate(b.createdAt) }}
        </span>
      </li>
    </ul>
  </div>

  <!-- Then collaborated -->
  <div v-if="doctorBlogs.collaborated?.length">
    <h3 class="text-lg font-semibold text-primaryText mb-3">协作文章</h3>
    <ul class="space-y-2">
      <li
        v-for="(b, i) in doctorBlogs.collaborated"
        :key="'collab-'+(b._id?.$oid || b._id || i)"
        class="flex items-start justify-between rounded-md border border-gray-200 px-3 py-2 hover:bg-gray-50"
      >
        <NuxtLink class="text-primaryColor underline" :to="`/blogs/${encodeURIComponent(b.slug)}`">
          {{ b.title }}
        </NuxtLink>
        <span class="text-xs text-gray-500 ml-3 whitespace-nowrap">
          {{ formatBlogDate(b.createdAt) }}
        </span>
      </li>
    </ul>
  </div>
</div>
<!-- End Doctor Blogs Card -->





          <!-- AIGC Section -->
          <div
            v-if="doctorDetails.provider.aigc_v3 && doctorDetails.provider.aigc_v3.chat_response && typeof doctorDetails.provider.aigc_v3.chat_response === 'object' && !Array.isArray(doctorDetails.provider.aigc_v3.chat_response)"
            class="bg-white p-4 sm:p-6 lg:p-8 rounded-md shadow-sm mt-6">
            <!-- Notice for user: AI generated info -->
            <div class="flex justify-center mb-4">
              <span
                class="inline-flex items-center justify-center rounded-full bg-primaryBtnHoverLite/50 px-3 py-1 text-xs font-medium text-primaryColor ring-1 ring-inset ring-primaryColor/20">
                注意：本页面信息由北美好医生根据多个数据源总结生成，信息可能存在误差，请通过诊所官方渠道核对
              </span>
            </div>

            <!-- Doctor Long Brief -->
            <section v-if="doctorDetails.provider.aigc_v3.chat_response.long_brief_chinese"
              class="mb-8 bg-gray-50 p-4 sm:p-6 sm:rounded-lg">
              <h2 class="text-xl sm:text-2xl font-semibold text-primaryText mb-2">
                {{ doctorDetails.provider.cn_name }}医生简介
              </h2>
              <p class="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                {{ doctorDetails.provider.aigc_v3.chat_response.long_brief_chinese }}
              </p>

              <button class="text-sm text-blue-600 underline mt-4" @click="toggleEnglish">
                {{ showEnglish ? 'Hide English' : 'Show English' }}
              </button>
              <transition name="fade">
                <div v-if="showEnglish">
                  <h2 class="text-xl sm:text-2xl font-semibold text-primaryText mb-2 mt-4">
                    About the Doctor (English)
                  </h2>
                  <p class="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                    {{ doctorDetails.provider.aigc_v3.chat_response.long_brief_english }}
                  </p>
                </div>
              </transition>
            </section>

            <!-- Doctor Background -->
            <section v-if="doctorDetails.provider.aigc_v3.chat_response.provider_background_chinese"
              class="mb-8 bg-gray-50 p-4 sm:p-6 sm:rounded-lg">
              <h2 class="text-xl sm:text-2xl font-semibold text-primaryText mb-2">
                {{ doctorDetails.provider.cn_name }}医生背景
              </h2>
              <p class="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                {{ doctorDetails.provider.aigc_v3.chat_response.provider_background_chinese }}
              </p>

              <button class="text-sm text-blue-600 underline mt-2" @click="toggleEnglish">
                {{ showEnglish ? 'Hide English' : 'Show English' }}
              </button>
              <transition name="fade">
                <div v-if="showEnglish">
                  <h2 class="text-xl sm:text-2xl font-semibold text-primaryText mb-2 mt-4">
                    Doctor's Background (English)
                  </h2>
                  <p class="text-gray-700 text-sm sm:text-base leading-relaxed whitespace-pre-wrap">
                    {{ doctorDetails.provider.aigc_v3.chat_response.provider_background_english }}
                  </p>
                </div>
              </transition>
            </section>

            <!-- Common Treated Conditions -->
            <section v-if="commonConditions && commonConditions.length"
              class="mb-8 bg-gray-50 p-4 sm:p-6 sm:rounded-lg">
              <h3 class="text-xl sm:text-2xl font-semibold text-primaryText mb-4">
                常见治疗疾病
              </h3>
              <div class="space-y-4">
                <div v-for="(cond, idx) in commonConditions" :key="idx"
                  class="border border-primaryBG-600 bg-primaryBG-100/50 rounded-lg p-4">
                  <h4 class="font-medium text-md text-primaryColor">
                    {{ cond.condition }}
                  </h4>
                  <p class="text-gray-700 text-sm sm:text-base mt-1">
                    {{ cond.description }}
                  </p>
                </div>
              </div>
            </section>

            <!-- Common Services Provided -->
            <section v-if="commonServices && commonServices.length" class="mb-8 bg-gray-50 p-4 sm:p-6 sm:rounded-lg">
              <h3 class="text-xl sm:text-2xl font-semibold text-primaryText mb-4">
                常见提供的服务
              </h3>
              <div class="space-y-4">
                <div v-for="(svc, idx) in commonServices" :key="idx"
                  class="border border-primaryBG-600 bg-primaryBG-100/50 rounded-lg p-4">
                  <h4 class="font-medium text-md text-primaryColor">
                    {{ svc.service }}
                  </h4>
                  <p class="text-gray-700 text-sm sm:text-base mt-1">
                    {{ svc.description }}
                  </p>
                </div>
              </div>
            </section>

            <!-- Insurance Remarks -->
            <section v-if="doctorDetails.provider.aigc_v3.chat_response.insruance_remarks"
              class="mb-8 bg-gray-50 p-4 sm:p-6 sm:rounded-lg">
              <h4 class="text-lg font-semibold text-primaryText mb-1">
                医疗保险
              </h4>
              <p class="text-gray-700 text-sm sm:text-base leading-relaxed">
                {{ doctorDetails.provider.aigc_v3.chat_response.insruance_remarks }}
              </p>
            </section>

            <!-- Other Remarks -->
            <section v-if="doctorDetails.provider.aigc_v3.chat_response.other_remarks"
              class="mb-8 bg-gray-50 p-4 sm:p-6 sm:rounded-lg">
              <h4 class="text-lg font-semibold text-primaryText mb-1">
                其他信息
              </h4>
              <p class="text-gray-700 text-sm sm:text-base leading-relaxed">
                {{ doctorDetails.provider.aigc_v3.chat_response.other_remarks }}
              </p>
              <!-- Clinic Website -->
              <div v-if="providerWebsite" class="">
                诊所网站:
                <a :href="providerWebsite" class="text-blue-600 underline break-words hover:text-blue-800"
                  target="_blank" rel="noopener">
                  {{ providerWebsite }}
                </a>
              </div>
            </section>
          </div>
          <!-- End of AIGC Section -->

          <!-- Fallback content if no aigc_v3 or invalid chat_response -->
          <div
            v-if="!doctorDetails.provider.aigc_v3 || !doctorDetails.provider.aigc_v3.chat_response || typeof doctorDetails.provider.aigc_v3.chat_response !== 'object' || Array.isArray(doctorDetails.provider.aigc_v3.chat_response)"
            class="mt-16 bg-white p-4 sm:p-6 lg:p-8 rounded-md shadow-sm">
            <h2 class="text-2xl sm:text-3xl font-semibold text-primaryText mb-4">
              {{ $t("doctor.detail_page.doctor_description") }}
            </h2>
            <div class="bg-gray-50 px-4 py-4 sm:rounded-lg">
              <!-- Special message for doctors with AIGC structure but invalid content -->
              <div
                v-if="doctorDetails.provider.aigc_v3 && (Array.isArray(doctorDetails.provider.aigc_v3.chat_response) || typeof doctorDetails.provider.aigc_v3.chat_response !== 'object')"
                class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p class="text-blue-800 text-sm">
                  <strong>📝 信息更新中</strong><br>
                  我们正在收集和整理{{ doctorDetails.provider.cn_name }}医师的详细资料，包括专业背景、服务项目等信息。请稍后再来查看，或直接联系诊所了解更多详情。
                </p>
              </div>

              <div
                v-if="doctorDetails.provider.doctor_description && doctorDetails.provider.doctor_description.length > 5">
                {{ doctorDetails.provider.doctor_description }}
              </div>
              <div v-else-if="doctorDetails.provider.internal_state === 'registered_approved'">
                {{ doctorDetails.provider.cn_name }}
                ({{ doctorDetails.provider.en_firstname }}
                {{ doctorDetails.provider.en_lastname }}) 医师致力于为患者提供专业的{{ doctorDetails.provider.cn_specialty_1
                }}医疗服务。诊所位于{{ doctorDetails.facilities[0].cn_full_address || doctorDetails.facilities[0].en_full_address
                }}，欢迎预约咨询。
              </div>
              <div v-else>
                <p class="text-gray-500 italic">医生信息正在完善中，请稍后查看或直接联系诊所咨询。</p>
              </div>
            </div>
          </div>


          <!-- Online Appointment Section -->
          <div class="mt-8 mb-8">
            <h2 class="pl-4 sm:pl-0 text-2xl font-semibold text-primaryText mb-4">
              {{ $t("doctor.online_appointment.title") }}
            </h2>
            <div class="bg-gray-100 px-4 py-6 sm:rounded-lg">
              <LazyClientOnly fallback-tag="span" fallback="Loading Scheduler...">
                <DoctorSchedulerDatePicker />
              </LazyClientOnly>
            </div>
          </div>

          <!-- Page Bottom Buttons (Appointment, Call) -->
          <div
            class="border-t border-gray-200 mt-8 pt-4 px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between">
            <div class="mb-4 md:mb-0">
              <h2 class="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
              </h2>
            </div>
            <div class="flex space-x-3">
              <NuxtLink
                :to="`/doctor/${doctorDetails.provider.cn_specialty_1}/${doctorDetails.provider.cn_name}/appointment`">
                <button type="button"
                  class="inline-flex items-center rounded-md bg-primaryBtn px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white hover:text-primaryBtn focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600">
                  {{ $t("doctor.listcard_online_appt") }}
                </button>
              </NuxtLink>


            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transportation + Map Section -->
    <div class="pb-4">

      <div id="clinic-map" class="mx-auto max-w-2xl sm:px-6 lg:max-w-7xl lg:px-8">
        <!-- Map -->

        <LazyDoctorDetailPageMap :googlemap_url="googlemap_url" />
        <!-- Transportation Info -->
        <div v-if="doctorDetails.provider.aigc_v3 && (transportationChinese || transportationEnglish)"
          class="mt-8 p-4 sm:p-6 sm:rounded-lg bg-gray-50">
          <h3 class="text-xl font-semibold text-primaryText mb-4">
            诊所附近公交与停车信息
          </h3>
          <p v-if="transportationChinese" class="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
            {{ transportationChinese }}
          </p>
          <!-- Collapsible English transportation -->
          <div v-if="transportationEnglish">
            <button class="text-sm text-blue-600 underline mb-3" @click="toggleEnglish">
              {{ showEnglish ? 'Hide English Transit Info' : 'Show English Transit Info' }}
            </button>
            <transition name="fade">
              <p v-if="showEnglish" class="text-gray-700 text-sm sm:text-base leading-relaxed">
                {{ transportationEnglish }}
              </p>
            </transition>
          </div>
        </div>




      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { HandThumbUpIcon, PhoneIcon } from '@heroicons/vue/20/solid';

const props = defineProps({
  doctorDetails: { type: Object, required: true },
});

// Appointment facility selection wiring
const { setTargetDoctorId, setTargetFacilityId } = usePatientAppointmentRequest()
const handleSelectFacility = (id) => {
  setTargetFacilityId(id)
}

// Initialize scheduling context on mount (default to first facility)
onMounted(() => {
  try {
    if (props.doctorDetails?.provider?._id) {
      setTargetDoctorId(String(props.doctorDetails.provider._id))
    }
    const firstFacilityId = props.doctorDetails?.facilities?.[0]?._id
    if (firstFacilityId) {
      setTargetFacilityId(String(firstFacilityId))
    }
  } catch (e) {
    console.debug('[DoctorDetailPageRoot] init scheduling context skipped:', e)
  }
})

/** For toggling the English text sections */
const showEnglish = ref(false);
const toggleEnglish = () => {
  showEnglish.value = !showEnglish.value;
};

/** Virtual Number Logic */
const virtualNumber = ref(null);
const displayPhoneNumber = computed(() => {
  if (!virtualNumber.value) return '';
  const digits = virtualNumber.value.replace(/\D/g, '');
  const phoneDigits =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (phoneDigits.length === 10) {
    return phoneDigits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  return virtualNumber.value;
});

const getVirtualNumber = async () => {
  try {
    const response = await $fetch('/api/telephony/doctor/getVirtualNumber', {
      params: {
        facility_id: props.doctorDetails.facilities[0]._id,
        doctor_name:
          props.doctorDetails.provider.en_firstname +
          ' ' +
          props.doctorDetails.provider.en_lastname,
        doctor_specialty: props.doctorDetails.provider.en_specialty_1,
      },
    });
    virtualNumber.value = response.virtual_number;
  } catch (err) {
    console.error(err);
  }
};

/** SEO Keywords */
const seoKeywordsChinese = computed(() =>
  props.doctorDetails.provider.aigc_v3?.chat_response?.seo_keywords_chinese ?? []
);
const seoKeywordsEnglish = computed(() =>
  props.doctorDetails.provider.aigc_v3?.chat_response?.seo_keywords_english ?? []
);
const showEnglishSEOKeywords = ref(false);

/** Clinic Website */
const providerWebsite = computed(() => {
  const site =
    props.doctorDetails.provider.aigc_v3?.chat_response?.provider_website?.trim();
  return site ? site : '';
});

/** Supported Languages */
const languagesChinese = computed(() =>
  props.doctorDetails.provider.aigc_v3?.chat_response
    ?.provider_supported_language_chinese ?? []
);
const languagesEnglish = computed(() =>
  props.doctorDetails.provider.aigc_v3?.chat_response
    ?.provider_supported_languages_english ?? []
);
const showEnglishLanguages = ref(false);

/** Common Conditions & Services */
const commonConditions = computed(() => {
  return (
    props.doctorDetails.provider.aigc_v3?.chat_response
      ?.common_treated_conditions ?? []
  );
});
const commonServices = computed(() => {
  return (
    props.doctorDetails.provider.aigc_v3?.chat_response
      ?.common_services_provided ?? []
  );
});

/** Transportation Info */
const transportationChinese = computed(() => {
  return props.doctorDetails.provider.aigc_v3?.chat_response
    ?.transportation_option_description_chinese;
});
const transportationEnglish = computed(() => {
  return props.doctorDetails.provider.aigc_v3?.chat_response
    ?.transportation_option_description_english;
});

/** Google Map */
const googlemap_url =
  props.doctorDetails.provider.internal_state === 'registered_approved'
    ? ref(
      `https://www.google.com/maps/embed/v1/place?key=AIzaSyDReyzGsSRITdRSalAgXhu_eyhpocJSD_c&q=${props.doctorDetails.facilities[0].mapbox_result.formattedAddress ||
      props.doctorDetails.facilities[0].mapbox_result.address
      }&language=zh-Hans`
    )
    : ref(
      `https://www.google.com/maps/embed/v1/place?key=AIzaSyDReyzGsSRITdRSalAgXhu_eyhpocJSD_c&q=${props.doctorDetails.facilities[0].mapbox_result.address
      }&language=zh-Hans`
    );


// --- Blogs: roles decided by API (reactive fetch) ---
const doctorBlogs = ref(null)

// normalize providers._id into a string for the API
function toIdParam(v) {
  if (!v) return ''
  if (typeof v === 'string') return v
  if (typeof v === 'object') {
    if (v.$oid) return String(v.$oid)
    if (typeof v.toHexString === 'function') return v.toHexString()
    if (v._id && typeof v._id === 'string') return v._id
  }
  return String(v)
}
const providerParamId = computed(() => toIdParam(props.doctorDetails?.provider?._id))

// don't call immediately to avoid 400 when provider id isn't ready
const { data: blogsData, error: blogsErr, refresh } = await useAsyncData(
  'blogs-by-provider',
  () => $fetch('/api/blogs/byProvider', { params: { providerId: providerParamId.value } }),
  { immediate: false }
)

// when provider id appears/changes, trigger the fetch
watchEffect(() => {
  if (providerParamId.value) refresh()
})

// assign reactively whenever data updates and there is no error
watch([blogsData, blogsErr], ([d, e]) => {
  if (d && !e) doctorBlogs.value = d
})


const formatBlogDate = (d) => {
  try {
    const dt = typeof d === 'string' ? new Date(d) : new Date(d?.$date || d)
    if (Number.isNaN(+dt)) return ''
    const yyyy = dt.getFullYear()
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    const dd = String(dt.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  } catch { return '' }
}


</script>

<style scoped>
/* Fade transition for toggling English text */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 300ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>